"use client";

import * as React from "react";
// `motion/react`, not `framer-motion`: this package declares `motion` v13, and every
// other component here imports from it. The old name only resolved on the branch
// point this file was written against.
import { motion, useReducedMotion } from "motion/react";
import {
	type CSSProperties,
	type ElementType,
	type JSX,
	memo,
	useMemo,
} from "react";

import { cn } from "../../lib/utils";

export interface ChatShimmerProps {
	children: string;
	as?: ElementType;
	className?: string;
	duration?: number;
	spread?: number;
}

const ShimmerComponent = ({
	children,
	as: Component = "p",
	className,
	duration = 2,
	spread = 2,
}: ChatShimmerProps) => {
	// motion.create builds a component type; memoizing still satisfies the compiler rule.
	const MotionComponent = useMemo(
		() => motion.create(Component as keyof JSX.IntrinsicElements),
		[Component],
	);

	const dynamicSpread = useMemo(
		() => (children?.length ?? 0) * spread,
		[children, spread],
	);
	const prefersReducedMotion = useReducedMotion();

	// Flat text for a reader who has asked the OS for no animation. Not merely a
	// stopped sweep: the shimmer paints by clipping the text to a moving gradient,
	// so leaving that in place with the motion removed would freeze the band
	// mid-sweep and light one part of the word differently from the rest.
	if (prefersReducedMotion) {
		return (
			<Component className={cn("inline-block text-neutral-500", className)}>
				{children}
			</Component>
		);
	}

	return (
		// eslint-disable-next-line react-hooks/static-components -- motion host tag is chosen by `as`; memoized MotionComponent is the supported API
		<MotionComponent
			animate={{ backgroundPosition: "0% center" }}
			className={cn(
				"relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent",
				// The sweep is a moving light band over a flat base colour. Both colours
				// carry literal fallbacks: the text is clipped to the background, so a
				// variable the host has not defined renders the label completely
				// invisible rather than merely off-palette.
				"[--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--shimmer-bg,#ffffff),#0000_calc(50%+var(--spread)))] [background-repeat:no-repeat,padding-box]",
				className,
			)}
			initial={{ backgroundPosition: "100% center" }}
			style={
				{
					"--spread": `${dynamicSpread}px`,
					backgroundImage:
						"var(--bg), linear-gradient(var(--shimmer-fg,#737373), var(--shimmer-fg,#737373))",
				} as CSSProperties
			}
			transition={{
				repeat: Number.POSITIVE_INFINITY,
				duration,
				ease: "linear",
			}}
		>
			{children}
		</MotionComponent>
	);
};

/** Sweeping highlight over a line of text, for a live status line. */
export const ChatShimmer = memo(ShimmerComponent);
ChatShimmer.displayName = "ChatShimmer";
